import { useState } from 'react';
import { Button, SearchField } from '@heroui/react';
import { ArrowRight, Building2, LockKeyhole, RefreshCw, SearchX } from 'lucide-react';
import type { Office } from '@odd/domain';
import { makeMatcher } from '../lib/search';
import { UserMenu } from '../components/UserMenu';
import './OfficeSelectionView.css';

function OfficeLandscape() {
  return (
    <svg className="office-entry-landscape" viewBox="0 0 420 245" fill="none" aria-hidden="true">
      <circle cx="316" cy="74" r="49" fill="#AF63FF" />
      <path d="m24 184 178-73 190 78-178 73Z" fill="#240952" />
      <path
        d="m24 184 178-73 190 78M74 205l178-74m-128 95 178-74m-128 95 178-74M73 164l190 78m-140-98 190 78m-140-98 190 78"
        stroke="#523476"
        strokeWidth=".75"
      />
      <path d="m66 136 70-29 59 24-70 29Z" fill="#E6D1FC" />
      <path d="m66 136 59 24v65l-59-24Z" fill="#AF9AEC" />
      <path d="m125 160 70-29v65l-70 29Z" fill="#8074DC" />
      <path d="m169 70 82-34 69 28-82 34Z" fill="#F6F5F4" />
      <path d="m169 70 69 28v142l-69-28Z" fill="#C0B0F6" />
      <path d="m238 98 82-34v142l-82 34Z" fill="#757CFC" />
      <path d="m281 158 52-22 43 18-52 22Z" fill="#E6D1FC" />
      <path d="m281 158 43 18v52l-43-18Z" fill="#AF9AEC" />
      <path d="m324 176 52-22v52l-52 22Z" fill="#8074DC" />
      {[0, 1, 2, 3].map((floor) => (
        <g key={floor} transform={`translate(0 ${floor * 28})`}>
          <path d="m184 92 14 6v17l-14-6Zm26 11 14 6v17l-14-6Z" fill="#42276F" />
          <path d="m252 108 14-6v17l-14 6Zm26-11 14-6v17l-14 6Z" fill={floor === 1 ? '#E6D1FC' : '#32235F'} />
        </g>
      ))}
      <path
        d="m80 156 12 5v17l-12-5Zm22 9 12 5v17l-12-5Zm37 7 12-5v17l-12 5Zm23-10 12-5v17l-12 5Zm133 16 12 5v17l-12-5Zm40 9 12-5v17l-12 5Z"
        fill="#42276F"
      />
      <path d="M351 38h15v15m0-15-23 23" stroke="#F6F5F4" strokeWidth="2" />
    </svg>
  );
}

interface OfficeSelectionViewProps {
  offices: Office[];
  email?: string;
  isLoading: boolean;
  error?: string;
  onSelect: (id: string) => void;
  onRetry: () => void;
  onLogout?: () => void;
}

const countLabel = (count: number) =>
  `${count.toLocaleString('es-ES')} ${count === 1 ? 'comunidad' : 'comunidades'}`;

export function OfficeSelectionView({
  offices,
  email,
  isLoading,
  error,
  onSelect,
  onRetry,
  onLogout,
}: OfficeSelectionViewProps) {
  const [query, setQuery] = useState('');
  const matches = makeMatcher(query);
  const visible = offices
    .filter((office) => matches(office.name))
    .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base', numeric: true }));

  return (
    <div className="office-entry">
      <header className="office-entry-header">
        <div className="office-entry-brand">
          <span>
            <img src="/brand/eleva-logo.svg" alt="Eleva" className="logo-light" width="100" height="28" />
            <img
              src="/brand/eleva-logo-white.svg"
              alt="Eleva"
              className="logo-dark"
              width="100"
              height="28"
            />
          </span>
          <span className="office-entry-product">
            Operativa
            <br />
            semanal
          </span>
        </div>
        <div className="office-entry-account">
          <UserMenu email={email} onLogout={onLogout} />
        </div>
      </header>

      <main className="office-entry-main">
        <aside className="office-entry-story" aria-label="Operativa semanal de tu oficina">
          <p className="office-entry-eyebrow">
            <span /> Una oficina. Un equipo.
          </p>
          <h2>
            La semana,
            <br />
            <span>con foco.</span>
          </h2>
          <p className="office-entry-story-description">
            Las prioridades claras. Los acuerdos compartidos.
            <br />
            El equipo en la misma dirección.
          </p>
          <OfficeLandscape />
          <div className="office-entry-story-footer">
            <span>
              De la visión compartida
              <br />
              al siguiente paso.
            </span>
            <ArrowRight size={22} strokeWidth={1.3} aria-hidden="true" />
          </div>
        </aside>

        <section className="office-entry-picker" aria-labelledby="office-entry-title" aria-busy={isLoading}>
          <div className="office-entry-intro">
            <p className="office-entry-kicker">Tu espacio de trabajo</p>
            <h1 id="office-entry-title">¿Dónde empezamos?</h1>
            <p>Elige una oficina para abrir su reunión operativa.</p>
          </div>

          {!error && (isLoading || offices.length > 0) && (
            <SearchField
              aria-label="Buscar oficina"
              value={query}
              onChange={setQuery}
              isDisabled={isLoading}
              className="office-entry-search"
              variant="secondary"
            >
              <SearchField.Group>
                <SearchField.SearchIcon />
                <SearchField.Input placeholder="Buscar por nombre de oficina…" />
                <SearchField.ClearButton aria-label="Borrar búsqueda" />
              </SearchField.Group>
            </SearchField>
          )}

          <div className="office-entry-list-heading">
            <h2>{query.trim() ? 'Resultados de búsqueda' : 'Tus oficinas'}</h2>
            {!isLoading && !error && (
              <span role="status" aria-live="polite" aria-atomic="true">
                {query.trim()
                  ? `${visible.length} de ${offices.length}`
                  : `${offices.length} ${offices.length === 1 ? 'disponible' : 'disponibles'}`}
              </span>
            )}
          </div>

          {isLoading ? (
            <>
              <span className="sr-only" role="status">
                Cargando tus oficinas…
              </span>
              <div className="office-entry-grid" aria-hidden="true">
                {Array.from({ length: 4 }, (_, i) => (
                  <div key={i} className="office-entry-skeleton">
                    <span />
                    <span />
                    <span />
                  </div>
                ))}
              </div>
            </>
          ) : error ? (
            <div className="office-entry-empty" role="alert">
              <RefreshCw size={25} strokeWidth={1.5} aria-hidden="true" />
              <h3>No pudimos cargar tus oficinas</h3>
              <p>{error}</p>
              <Button variant="secondary" size="sm" onPress={onRetry}>
                Volver a intentarlo
              </Button>
            </div>
          ) : offices.length === 0 ? (
            <div className="office-entry-empty">
              <Building2 size={27} strokeWidth={1.4} aria-hidden="true" />
              <h3>Tu próxima reunión empieza aquí</h3>
              <p>
                Tu cuenta todavía no tiene oficinas visibles. Si esperabas ver alguna, consulta la asignación
                de tus comunidades con tu responsable.
              </p>
            </div>
          ) : visible.length === 0 ? (
            <div className="office-entry-empty">
              <SearchX size={27} strokeWidth={1.4} aria-hidden="true" />
              <h3>No encontramos esa oficina</h3>
              <p>Prueba con otro nombre o vuelve a ver todas tus oficinas.</p>
              <Button variant="secondary" size="sm" onPress={() => setQuery('')}>
                Ver todas las oficinas
              </Button>
            </div>
          ) : (
            <ul className="office-entry-grid" aria-label="Oficinas disponibles">
              {visible.map((office) => (
                <li key={office.id}>
                  <button
                    type="button"
                    className="office-entry-card"
                    onClick={() => onSelect(office.id)}
                    aria-label={`Abrir ${office.name}, ${countLabel(office.communities)}`}
                  >
                    <span className="office-entry-card-icon">
                      <Building2 size={19} strokeWidth={1.4} aria-hidden="true" />
                    </span>
                    <span className="office-entry-card-copy">
                      <strong>{office.name}</strong>
                      <span>{countLabel(office.communities)}</span>
                    </span>
                    <span className="office-entry-card-arrow">
                      <ArrowRight size={17} aria-hidden="true" />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <p className="office-entry-hint">
            <LockKeyhole size={13} aria-hidden="true" /> Solo las oficinas a las que tienes acceso.
          </p>
        </section>
      </main>

      <footer className="office-entry-footer">
        <span>Hecho por Eleva. Para trabajar mejor.</span>
        <span>Podrás cambiar de oficina desde la cabecera.</span>
      </footer>
    </div>
  );
}
